import React, { useState, useEffect, useRef, useMemo } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { StyledReasonWrapper } from "./styled";
import ImageUpload from "./ImageUpload";
import { getReasons, getReasonFirstReasons, getS3SignedUrls } from "utils/api";
import { useDispatch, useSelector } from "react-redux";
import { getMerchantConfig, getRmsConfig } from "features/config/configSlice";
import {
  addCustomerComment,
  addProceedBool,
  addSelectedReason,
  addSelectedSubReason,
  addUploadMediaIds,
  getPreviews,
  getCustomerComment,
  getProductid,
  getReplacementMode,
  getSelectedGroupProducts,
  getSelectedProduct,
  getSelectedReason,
  getSelectedSubReason,
  getUploads,
  getUploadMediaIds,
  setImageUploads,
  addUploads,
} from "features/product/productSlice";
import {
  addReturnedProducts,
  getReturnedProducts,
} from "features/returnedProducts/returnedProductSlice";
import Compressor from "compressorjs";
import { getSelectedOrder } from "features/orders/orderSlice";
import { toast } from "react-toastify";
import { sendWebhook } from "utils/axiosInstance";
import { getLineItemMetadata } from "utils/lineItemMetadata";
import {
  areReturnMediaRequirementsMet,
  buildReturnMediaPresignFiles,
  getReasonMinimumConfiguredMediaUploads,
  getReasonMinimumMediaUploads,
  hasReturnMediaReasonIdentity,
  reasonRequiresMedia,
} from "utils/returnMedia";
import LoadingError from "components/v2/Common/LoadingError";
import { getEffectiveReasonRule } from "utils/reasonResolution";
import { getReasonFirstRefundMethodOptions } from "utils/reasonFirstRefund";
import { additionalDetailsActive, additionalCommentPayload, additionalCommentValid, updateQuestionAnswer, MAX_ADDITIONAL_COMMENT_LENGTH } from "utils/questionnaireDetails";

const reasonsCache = new Map();
const reasonDraftCache = new Map();

const getDraftKey = (lineItemId) =>
  lineItemId !== undefined && lineItemId !== null ? String(lineItemId) : null;

const getReasonDraft = (lineItemId) => {
  const draftKey = getDraftKey(lineItemId);
  return draftKey ? reasonDraftCache.get(draftKey) || {} : {};
};

const updateReasonDraft = (lineItemId, patch) => {
  const draftKey = getDraftKey(lineItemId);
  if (!draftKey) {
    return;
  }

  reasonDraftCache.set(draftKey, {
    ...(reasonDraftCache.get(draftKey) || {}),
    ...patch,
  });
};

export const clearReasonDraft = (lineItemId) => {
  const draftKey = getDraftKey(lineItemId);
  if (draftKey) {
    reasonDraftCache.delete(draftKey);
  }
};

const shuffleArray = (items = []) => {
  const shuffledItems = [...items];
  for (let index = shuffledItems.length - 1; index > 0; index--) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffledItems[index], shuffledItems[randomIndex]] = [
      shuffledItems[randomIndex],
      shuffledItems[index],
    ];
  }
  return shuffledItems;
};

const sanitizeUploadFilename = (filename) => {
  const rawName = String(filename || "")
    .replace(/\\/g, "/")
    .split("/")
    .pop()
    .trim();
  const normalizedName = rawName
    .normalize("NFKD")
    .split("")
    .filter((char) => char.charCodeAt(0) <= 127)
    .join("");
  const safeName = normalizedName
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/^[._-]+|[._-]+$/g, "");

  if (!safeName) {
    return `upload_${Date.now()}`;
  }

  const extensionIndex = safeName.lastIndexOf(".");
  const hasExtension = extensionIndex > 0;
  const stem = (hasExtension ? safeName.slice(0, extensionIndex) : safeName)
    .slice(0, 100)
    .replace(/^[._-]+|[._-]+$/g, "") || "upload";
  const extension = hasExtension
    ? safeName
        .slice(extensionIndex)
        .toLowerCase()
        .replace(/[^A-Za-z0-9.]/g, "")
        .slice(0, 16)
    : "";

  return `${stem}${extension}`;
};

function Reason({
  onValidityChange,
  onSelectedCountChange,
  onBusyChange,
  registerSubmit,
  validationAttempted = false,
  onValidationReset,
  onResolutionSelect,
  availableResolutions = [],
}) {
  const merchantConfig = useSelector(getMerchantConfig);
  const rmsConfig = useSelector(getRmsConfig);
  const selectedOrder = useSelector(getSelectedOrder);
  const selectedProduct = useSelector(getSelectedProduct);
  const selectedGroupProducts = useSelector(getSelectedGroupProducts);
  const preReason = useSelector(getSelectedReason);
  const preSubReason = useSelector(getSelectedSubReason);
  const returnedProducts = useSelector(getReturnedProducts);
  const productid = useSelector(getProductid);
  const isReplacement = useSelector(getReplacementMode);
  const storedCustomerComment = useSelector(getCustomerComment);
  const preUploads = useSelector(getUploads);
  const prePreviews = useSelector(getPreviews);
  const draftLineItemId = selectedOrder?.order_id
    ? `${selectedOrder.order_id}:${selectedProduct?.line_item_id}`
    : selectedProduct?.line_item_id;
  const initialDraft = getReasonDraft(draftLineItemId);
  const savedItem = returnedProducts.find((item) =>
    String(item.line_item_id) === String(selectedProduct?.line_item_id) &&
    item.customer_reason_code === preReason?.reason_code,
  );
  const savedAnswers = Object.fromEntries(
    (savedItem?.questionnaire_answers?.length
      ? savedItem.questionnaire_answers
      : savedItem?.warranty_questionnaire_answers || preReason?.questionnaire_answers || [])
      .map((answer) => [String(answer.question_id ?? answer.question_id_snapshot), answer]),
  );
  const savedAnswersRef = useRef(savedAnswers);
  savedAnswersRef.current = savedAnswers;

  const [selectedReason, setSelectedReason] = useState(preReason);
  const [selectedSubReason, setSelectedSubReason] = useState(preSubReason);
  const [selectedRefundMethod, setSelectedRefundMethod] = useState(
    initialDraft.customerPreferredRefundMethod || savedItem?.customer_preferred_refund_method || "",
  );
  const [preview, setPreview] = useState(initialDraft.preview || prePreviews);
  const [uploads, setUploads] = useState(initialDraft.uploads || preUploads);
  const quantityInc = selectedProduct.remaining_qty;
  const [reasons, setReasons] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [reasonLoading, setReasonLoading] = useState(true);
  const [verificationLoader, setVerificationLoader] = useState(false);
  const [reasonApiError, setReasonApiError] = useState(false);
  const [otherReason, setOtherReason] = useState(
    initialDraft.comment ?? storedCustomerComment,
  );
  const [questionAnswers, setQuestionAnswers] = useState(
    initialDraft.questionAnswers || savedAnswers,
  );
  const preUploadMediaIds = useSelector(getUploadMediaIds);
  const [uploadMediaIds, setUploadMediaIds] = useState(
    initialDraft.uploadMediaIds || preUploadMediaIds,
  );
  const [imageUploadCount, setImageUploadCount] = useState(0);
  const [videoUploadCount, setVideoUploadCount] = useState(0);
  const [uploadSuccessMessage, setUploadSuccessMessage] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownDirections, setDropdownDirections] = useState({});
  const inlineScrollTopRef = useRef(0);
  const handleProceedRef = useRef(null);
  const uploadMediaIdsRef = useRef(preUploadMediaIds || []);
  const otherReasonRef = useRef(storedCustomerComment || "");
  const storedCustomerCommentRef = useRef(storedCustomerComment || "");
  const canProceedRef = useRef(false);
  const verificationLoaderRef = useRef(false);
  const uploadingRef = useRef(false);
  const isExchangeFlowRef = useRef(false);
  const onValidityChangeRef = useRef(onValidityChange);
  const onBusyChangeRef = useRef(onBusyChange);
  const onSelectedCountChangeRef = useRef(onSelectedCountChange);
  const lastValidityRef = useRef(null);
  const lastBusyRef = useRef(null);
  const lastSelectedCountRef = useRef(null);

  const dispatch = useDispatch();
  const reasonFirstFlow =
    rmsConfig?.customer_flow_preference === "reason_first" &&
    !productid?.includes("warranty_claim");

  const preservePageScroll = (callback) => {
    const scrollingElement =
      document.scrollingElement || document.documentElement || document.body;
    inlineScrollTopRef.current =
      scrollingElement?.scrollTop || window.scrollY || 0;
    callback();
    requestAnimationFrame(() => {
      const top = inlineScrollTopRef.current || 0;
      if (scrollingElement) {
        scrollingElement.scrollTop = top;
      } else {
        window.scrollTo(0, top);
      }
    });
  };

  const isExchangeFlow =
    selectedReason?.selected_resolution === "exchange" ||
    selectedReason?.selected_resolution === "replacement" ||
    (!selectedReason?.selected_resolution && productid !== null &&
      productid !== undefined &&
      productid.indexOf("exchange") !== -1);
  const effectiveReasonRule = getEffectiveReasonRule(selectedReason, selectedSubReason);
  const effectiveResolutionConfig = effectiveReasonRule.resolution_config || {};
  const refundBucket = selectedOrder?.is_cod || (
    rmsConfig.partially_paid_order_as_cod &&
    selectedOrder?.financial_status === "partially_paid"
  )
    ? "cod"
    : selectedOrder?.financial_status === "partially_paid"
      ? "partial"
      : "prepaid";
  const refundMethodOptions = useMemo(() => {
    const refundConfig = effectiveResolutionConfig.refund || {};
    return reasonFirstFlow
      ? getReasonFirstRefundMethodOptions(
        {
          reason_resolution_config: refundConfig,
          applicable_refund_modes: selectedProduct?.applicable_refund_modes,
        },
        refundBucket,
        rmsConfig,
        selectedOrder,
      )
        : [];
  }, [reasonFirstFlow, effectiveResolutionConfig.refund, selectedProduct?.applicable_refund_modes, refundBucket, rmsConfig, selectedOrder]);

  useEffect(() => {
    const isRefund = selectedReason?.selected_resolution === "refund";
    if (!isRefund) {
      if (selectedRefundMethod) setSelectedRefundMethod("");
      return;
    }
    if (refundMethodOptions.some((option) => option.value === selectedRefundMethod)) return;
    setSelectedRefundMethod(refundMethodOptions.length === 1 ? refundMethodOptions[0].value : "");
  }, [refundMethodOptions, selectedReason?.selected_resolution, selectedRefundMethod]);

  useEffect(() => {
    updateReasonDraft(draftLineItemId, {
      customerPreferredRefundMethod: selectedRefundMethod,
    });
  }, [draftLineItemId, selectedRefundMethod]);

  useEffect(() => {
    storedCustomerCommentRef.current = storedCustomerComment || "";
  }, [storedCustomerComment]);

  useEffect(() => {
    onValidityChangeRef.current = onValidityChange;
  }, [onValidityChange]);

  useEffect(() => {
    onBusyChangeRef.current = onBusyChange;
  }, [onBusyChange]);

  useEffect(() => {
    onSelectedCountChangeRef.current = onSelectedCountChange;
  }, [onSelectedCountChange]);

  useEffect(() => {
    const reasonType = reasonFirstFlow
      ? "reason_first"
      : productid?.includes("warranty_claim")
      ? "warranty_claim"
      : isReplacement
        ? "replacement"
        : productid !== null && productid.indexOf("return") !== -1
          ? "refund"
          : "exchange";
    const shouldShuffleReasons = Boolean(rmsConfig?.randomised_reason_order);
    const warrantyExpired =
      reasonType === "warranty_claim" &&
      (selectedProduct?.warranty_ineligible_reason === "warranty_expired" ||
        selectedProduct?.warranty_expired_but_service_available === true ||
        selectedProduct?.warranty_claim_reason_mode === "service_repair_only");
    const reasonParams =
      reasonType === "warranty_claim" &&
      (selectedProduct?.warranty_is_lifetime === true || warrantyExpired)
        ? {
            warranty_is_lifetime: selectedProduct?.warranty_is_lifetime,
            warranty_expired: warrantyExpired,
          }
        : {};
    const orderId = selectedOrder?.order_id;
    const sourceLineItemId = selectedProduct?.line_item_id;
    const cacheKey = `${reasonType}|${orderId || ""}|${sourceLineItemId || ""}|${selectedProduct.product_id}|lifetime:${Boolean(
      reasonParams.warranty_is_lifetime,
    )}|expired:${Boolean(warrantyExpired)}|shuffle:${shouldShuffleReasons}`;
    const cachedReasons = reasonsCache.get(cacheKey);

    if (cachedReasons && cachedReasons.length > 0) {
      setReasons(cachedReasons);
      setReasonLoading(false);
      return;
    }

    const reasonRequest = reasonFirstFlow
      ? getReasonFirstReasons({
          order_id: orderId,
          source_line_item_id: sourceLineItemId,
        })
      : Object.keys(reasonParams).length > 0
        ? getReasons(reasonType, selectedProduct.product_id, reasonParams)
        : getReasons(reasonType, selectedProduct.product_id);

    reasonRequest
      .then((resp) => {
        const apiReasons = resp?.data?.reasons || [];
        const nextReasons = shouldShuffleReasons
          ? shuffleArray(apiReasons)
          : apiReasons;
        reasonsCache.set(cacheKey, nextReasons);
        setReasons(nextReasons);
        setReasonLoading(false);
      })
      .catch(() => {
        setReasonApiError(true);
      });
  }, [
    isReplacement,
    productid,
    reasonFirstFlow,
    rmsConfig?.randomised_reason_order,
    rmsConfig?.customer_flow_preference,
    selectedProduct.product_id,
    selectedProduct?.line_item_id,
    selectedOrder?.order_id,
    selectedProduct?.warranty_ineligible_reason,
    selectedProduct?.warranty_is_lifetime,
  ]);

  useEffect(() => {
    const draft = getReasonDraft(draftLineItemId);
    const nextUploadMediaIds = draft.uploadMediaIds || preUploadMediaIds || [];
    setUploadMediaIds(nextUploadMediaIds);
    uploadMediaIdsRef.current = nextUploadMediaIds;
  }, [draftLineItemId, preUploadMediaIds]);

  useEffect(() => {
    uploadMediaIdsRef.current = uploadMediaIds || [];
    updateReasonDraft(draftLineItemId, {
      uploadMediaIds: uploadMediaIds || [],
    });
  }, [draftLineItemId, uploadMediaIds]);

  useEffect(() => {
    updateReasonDraft(draftLineItemId, { uploads: uploads || [] });
  }, [uploads, draftLineItemId]);

  useEffect(() => {
    updateReasonDraft(draftLineItemId, { preview: preview || [] });
  }, [preview, draftLineItemId]);

  useEffect(() => {
    const draft = getReasonDraft(draftLineItemId);
    const nextUploads = draft.uploads || preUploads || [];
    const nextPreview = draft.preview || prePreviews || [];
    setUploads(nextUploads);
    setPreview(nextPreview);
    setImageUploadCount(
      nextUploads.filter((file) => file?.type?.startsWith("image/")).length,
    );
    setVideoUploadCount(
      nextUploads.filter((file) => file?.type?.startsWith("video/")).length,
    );
  }, [draftLineItemId, prePreviews, preUploads]);

  useEffect(() => {
    const draft = getReasonDraft(draftLineItemId);
    const nextComment = draft.comment ?? storedCustomerCommentRef.current;
    setOtherReason(nextComment);
    otherReasonRef.current = nextComment || "";
    setQuestionAnswers(draft.questionAnswers || savedAnswersRef.current);
  }, [draftLineItemId]);

  useEffect(() => {
    otherReasonRef.current = otherReason || "";
    updateReasonDraft(draftLineItemId, { comment: otherReason || "" });
  }, [draftLineItemId, otherReason]);

  useEffect(() => {
    updateReasonDraft(draftLineItemId, {
      questionAnswers: questionAnswers || {},
    });
  }, [draftLineItemId, questionAnswers]);

  useEffect(() => {
    verificationLoaderRef.current = verificationLoader;
  }, [verificationLoader]);

  useEffect(() => {
    uploadingRef.current = uploading;
  }, [uploading]);

  useEffect(() => {
    isExchangeFlowRef.current = isExchangeFlow;
  }, [isExchangeFlow]);

  useEffect(() => {
    if (!selectedReason || reasons.length === 0) {
      return;
    }

    const selectedCode = selectedReason.reason_code || selectedReason.id;
    const fullReason = reasons.find(
      (reasonItem) =>
        String(reasonItem.reason_code || reasonItem.id) ===
        String(selectedCode),
    );

    if (!fullReason) {
      return;
    }

    const needsHydration =
      selectedReason.is_media_required === undefined ||
      selectedReason.collect_customer_comment === undefined ||
      (selectedReason.sub_reasons || []).length !==
        (fullReason.sub_reasons || []).length;

    if (needsHydration) {
      const hydratedReason = {
        ...fullReason,
        ...(selectedReason.selected_resolution
          ? { selected_resolution: selectedReason.selected_resolution }
          : {}),
        ...(selectedReason.customer_preferred_refund_method
          ? { customer_preferred_refund_method: selectedReason.customer_preferred_refund_method }
          : {}),
        is_media_required: fullReason.is_media_required ?? false,
        collect_customer_comment: fullReason.collect_customer_comment ?? false,
        ...(reasonFirstFlow ? { reason_mode: "reason_first" } : {}),
      };
      setSelectedReason(hydratedReason);
      dispatch(addSelectedReason(hydratedReason));
    }

    if (selectedSubReason) {
      const selectedSubCode =
        selectedSubReason.reason_code || selectedSubReason.id;
      const fullSubReason = (fullReason.sub_reasons || []).find(
        (subReasonItem) =>
          String(subReasonItem.reason_code || subReasonItem.id) ===
          String(selectedSubCode),
      );

      if (fullSubReason && selectedSubReason.is_media_required === undefined) {
        setSelectedSubReason(fullSubReason);
        dispatch(addSelectedSubReason(fullSubReason));
      }
    }
  }, [dispatch, reasonFirstFlow, reasons, selectedReason, selectedSubReason]);

  const dispatchReturnedProducts = () => {
    let tempProducts = [...returnedProducts];
    let line_item_ids = tempProducts.map((a) => String(a.line_item_id));
    const resolvedReturnType = selectedReason?.selected_resolution || (productid?.includes("warranty_claim")
      ? "warranty_claim"
      : isReplacement
        ? "replacement"
        : productid !== null && productid.indexOf("return") !== -1
          ? "refund"
          : "exchange");
    const selectedResolutionConfig = selectedReason?.reason_mode === "reason_first"
      ? effectiveResolutionConfig?.[resolvedReturnType] || {}
      : null;

    if (selectedGroupProducts.length > 0) {
      for (let i = selectedGroupProducts.length - 1; i >= 0; i--) {
        const product = selectedGroupProducts[i];

        const index = line_item_ids.indexOf(String(product.line_item_id));

        if (index !== -1) {
          tempProducts.splice(index, 1);
          line_item_ids.splice(index, 1);
        }
      }
    } else {
      if (line_item_ids.indexOf(String(selectedProduct.line_item_id)) !== -1) {
        let index = line_item_ids.indexOf(String(selectedProduct.line_item_id));
        tempProducts.splice(index, 1);
      }
    }

    let childProducts = selectedGroupProducts.filter(
      (groupProduct) =>
        groupProduct.is_child === true &&
        groupProduct.refund_eligible === true &&
        groupProduct.auto_add === true,
    );
    const currentMediaIds = uploadMediaIdsRef.current || [];
    const selectedLineMedia = currentMediaIds.find(
      (item) =>
        String(item.line_item_id) === String(selectedProduct.line_item_id),
    );
    const questionnaireAnswers = buildQuestionnaireAnswerPayload();

    setUploads([]);
    dispatch(addUploads([]));
    const nextReturnedProducts = [
      ...tempProducts,
      {
        merchant_id: merchantConfig.merchant_id,
        line_item_id: selectedProduct.line_item_id,

        product_id: selectedProduct.product_id,
        variant_id: selectedProduct.variant_id,
        product_price: selectedProduct.product_price,
        quantity: quantityInc,
        product_title: selectedProduct.product_title,
        variant_name: selectedProduct.variant_name,
        product_sku: selectedProduct.sku,
        original_product_price: selectedProduct.original_product_price,
        variant_weight: selectedProduct?.variant_weight || "",
        variant_weight_unit: selectedProduct?.variant_weight_unit || "",
        variant_image_url: selectedProduct.image_url,

        return_type: resolvedReturnType,
        ...(selectedReason.reason_mode === "reason_first"
          ? {
              reason_mode: "reason_first",
              reason_resolution_config: selectedResolutionConfig,
              ...(resolvedReturnType === "refund"
                ? { customer_preferred_refund_method: selectedRefundMethod || null }
                : {}),
            }
          : {}),

        customer_reason_code: selectedReason.reason_code,
        customer_reason_label: selectedReason.reason_label,
        customer_reason_comment: otherReason,
        questionnaire_answers: questionnaireAnswers,
        customer_sub_reason_code:
          selectedSubReason !== undefined && selectedSubReason !== null
            ? selectedSubReason.reason_code
            : null,
        customer_sub_reason_label:
          selectedSubReason !== undefined && selectedSubReason !== null
            ? selectedSubReason.reason_label
            : null,

        total_item_discount: selectedProduct.total_item_discount,
        discounted_price: selectedProduct.discounted_price,

        exchange_applicable: selectedProduct.exchange_applicable,
        refund_applicable: selectedProduct.refund_applicable,
        shipment_applicable: resolvedReturnType === "connect_to_agent" ? false : selectedResolutionConfig?.shipment_applicable ?? selectedProduct.shipment_applicable,

        product_group_id: selectedProduct.product_group_id,
        is_parent: selectedProduct.is_parent,
        is_child: selectedProduct.is_child,
        properties: selectedProduct.properties,

        remaining_qty: selectedProduct.quantity - quantityInc,
        applicable_refund_modes: selectedProduct.applicable_refund_modes,
        warranty_claim_flow_type: selectedReason.reason_flow_type,
        warranty_claim_flow_label:
          selectedReason.reason_flow_type === "general_service"
            ? "General Service"
            : selectedReason.reason_flow_type === "damage_repair"
              ? "Damage Repair"
              : "Warranty Claim",
        uploaded_media: selectedLineMedia?.uploads || [],
        uploaded_media_ids: (selectedLineMedia?.uploads || []).map(
          (mediaItem) => mediaItem.media,
        ),
        media_previews: (selectedLineMedia?.uploads || []).map(
          (mediaItem) => mediaItem.url,
        ),
        ...getLineItemMetadata(selectedProduct),
      },
      ...childProducts.map((childProduct) => ({
        merchant_id: merchantConfig.merchant_id,
        line_item_id: childProduct.line_item_id,

        product_id: childProduct.product_id,
        variant_id: childProduct.variant_id,
        product_price: childProduct.product_price,
        quantity: quantityInc,
        product_title: childProduct.product_title,
        variant_name: childProduct.variant_name,
        product_sku: childProduct.sku,
        original_product_price: childProduct.original_product_price,
        variant_weight: childProduct?.variant_weight || "",
        variant_weight_unit: childProduct?.variant_weight_unit || "",
        variant_image_url: childProduct.image_url,

        return_type: resolvedReturnType,
        ...(selectedReason.reason_mode === "reason_first"
          ? {
              reason_mode: "reason_first",
              reason_resolution_config: selectedResolutionConfig,
              ...(resolvedReturnType === "refund"
                ? { customer_preferred_refund_method: selectedRefundMethod || null }
                : {}),
            }
          : {}),

        customer_reason_code: selectedReason.reason_code,
        customer_reason_label: selectedReason.reason_label,
        customer_reason_comment: otherReason,
        questionnaire_answers: questionnaireAnswers,
        customer_sub_reason_code:
          selectedSubReason !== undefined && selectedSubReason !== null
            ? selectedSubReason.reason_code
            : null,
        customer_sub_reason_label:
          selectedSubReason !== undefined && selectedSubReason !== null
            ? selectedSubReason.reason_label
            : null,

        total_item_discount: childProduct.total_item_discount,
        discounted_price: childProduct.discounted_price,

        exchange_applicable: childProduct.exchange_applicable,
        refund_applicable: childProduct.refund_applicable,
        shipment_applicable: resolvedReturnType === "connect_to_agent" ? false : selectedResolutionConfig?.shipment_applicable ?? childProduct.shipment_applicable,

        product_group_id: childProduct.product_group_id,
        is_parent: childProduct.is_parent,
        is_child: childProduct.is_child,
        properties: childProduct.properties,

        remaining_qty: childProduct.quantity - quantityInc,
        applicable_refund_modes: selectedProduct.applicable_refund_modes,
        warranty_claim_flow_type: selectedReason.reason_flow_type,
        warranty_claim_flow_label:
          selectedReason.reason_flow_type === "general_service"
            ? "General Service"
            : selectedReason.reason_flow_type === "damage_repair"
              ? "Damage Repair"
              : "Warranty Claim",
        ...getLineItemMetadata(childProduct),
      })),
    ];

    dispatch(addReturnedProducts(nextReturnedProducts));
    dispatch(addProceedBool(true));
    setVerificationLoader(false);

    return nextReturnedProducts;
  };

  const isOtherReason = (reason) =>
    reason?.reason_code === "other" ||
    (reason?.reason_code || "").toLowerCase().includes("other") ||
    (reason.collect_customer_comment && reason.mandate_customer_comment);

  const isValidUploads = () =>
    uploads.length &&
    merchantConfig?.merchant_id &&
    hasReturnMediaReasonIdentity(selectedReason) &&
    selectedOrder?.order_id;

  const getSelectedReasonQuestions = () =>
    Array.isArray(selectedReason?.reason_questions)
      ? selectedReason.reason_questions
      : [];

  const getQuestionAnswer = (questionId) =>
    questionAnswers?.[String(questionId)] || {};

  const setQuestionAnswer = (question, patch) => {
    const questionId = String(question.id);
    if (onValidationReset) onValidationReset();
    setQuestionAnswers((prev) => ({
      ...(prev || {}),
      [questionId]: updateQuestionAnswer(question, prev?.[questionId] || {}, {
        question_id: question.id,
        question_code: question.code,
        question_label: question.title,
        ...patch,
      }),
    }));
  };

  const buildQuestionnaireAnswerPayload = () =>
    getSelectedReasonQuestions()
      .map((mapping) => {
        const question = mapping.question || {};
        const answer = getQuestionAnswer(question.id);
        return {
          question_id: question.id,
          question_code: question.code,
          question_label: question.title,
          answer_value: answer.answer_value ?? null,
          answer_label: answer.answer_label ?? null,
          answer_text: answer.answer_text ?? null,
          option_id: answer.option_id ?? null,
          ...additionalCommentPayload(question, answer),
        };
      })
      .filter(
        (answer) =>
          answer.answer_value !== null ||
          answer.answer_text !== null ||
          answer.option_id !== null,
      );

  const areRequiredQuestionsAnswered = () =>
    getSelectedReasonQuestions().every((mapping) => {
      const question = mapping.question || {};
      const answer = getQuestionAnswer(question.id);
      if (!additionalCommentValid(question, answer)) return false;
      if (!mapping.required) return true;
      if (question.answer_type === "text") {
        return Boolean((answer.answer_text || "").trim());
      }
      return answer.answer_value !== undefined && answer.answer_value !== null;
    });

  const handleMediaUpload = async (files) => {
    return getS3SignedUrls(
      files,
      selectedOrder.order_id,
      selectedOrder.order_number,
      selectedProduct?.line_item_id,
    );
  };

  const getSignedMediaUrl = (post) => {
    const baseUrl = post?.post_data?.url || "";
    const key = post?.post_data?.fields?.key || "";
    if (!baseUrl || !key) {
      return "";
    }
    return `${baseUrl.replace(/\/$/, "")}/${key.replace(/^\//, "")}`;
  };

  const logUploadError = (err, formData, type) => {
    const files = Array.from(formData?.getAll("files") || []).map((f) => ({
      name: f.name,
      type: f.type,
      size: f.size,
    }));
    const ua = navigator.userAgent || "";
    const is_webview = /(WebView|wv)/i.test(ua);
    let webview_version = null;
    const match = ua.match(/WebView\/(\d+(?:\.\d+)*)/i);
    if (match) {
      webview_version = match[1];
    }
    try {
      const payload = {
        err,
        type,
        reason: selectedReason,
        mid: merchantConfig.merchant_id,
        files,
        formData,
        userAgent: navigator.userAgent,
        is_webview,
      };
      if (webview_version) {
        payload.webview_version = webview_version;
      }
      sendWebhook(payload);
    } catch (_) {}
  };

  const compressFile = (file) => {
    return new Promise((resolve) => {
      if (file.type.startsWith("image/")) {
        new Compressor(file, {
          quality: 0.6,
          convertSize: 1000000,
          success(result) {
            const compressed = new File([result], file.name, {
              type: result.type,
            });
            compressed.media_config_id = file.media_config_id;
            compressed.capture_code = file.capture_code;
            compressed.capture_label = file.capture_label;
            resolve(compressed);
          },
          error() {
            resolve(file);
          },
        });
      } else {
        resolve(file);
      }
    });
  };

  async function uploadToS3(file, postData, onProgress) {
    const formData = new FormData();
    Object.entries(postData.fields).forEach(([k, v]) => formData.append(k, v));
    formData.append("file", file);

    const response = await fetch(postData.url, {
      method: "POST",
      body: formData,
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Upload failed: ${response.status} ${text}`);
    }
    if (onProgress) onProgress();
    return true;
  }

  const isBrowserFile = (file) =>
    typeof File !== "undefined" && file instanceof File;

  const getUpdatedUploads = (lineItemId, newUploads) => {
    const i = uploadMediaIds.findIndex(
      (u) => String(u.line_item_id) === String(lineItemId),
    );
    const mergeUploads = (existingUploads = []) => {
      const mergedUploads = [...existingUploads];
      newUploads.forEach((newUpload) => {
        const alreadyExists = mergedUploads.some(
          (upload) =>
            (newUpload.media &&
              upload.media &&
              String(upload.media) === String(newUpload.media)) ||
            (newUpload.name &&
              upload.name &&
              String(upload.name) === String(newUpload.name)),
        );
        if (!alreadyExists) {
          mergedUploads.push(newUpload);
        }
      });
      return mergedUploads;
    };

    return i !== -1
      ? uploadMediaIds.map((u, idx) =>
          idx === i ? { ...u, uploads: mergeUploads(u.uploads || []) } : u,
        )
      : [
          ...uploadMediaIds,
          { line_item_id: lineItemId, uploads: [...newUploads] },
        ];
  };

  const processProceed = async (onSuccess, type) => {
    setUploadError(null);

    try {
      if (reasonRequiresMedia(selectedReason)) {
        if (!isValidUploads()) {
          toast.error("Please retry uploading images");
          return;
        }

        const matchingLineItem = uploadMediaIds.find(
          (item) =>
            String(item.line_item_id) === String(selectedProduct.line_item_id),
        );

        let newIndices = [];
        const currentUploads = Array.isArray(uploads) ? uploads : [];
        const uploadedNames = new Set(
          (matchingLineItem?.uploads || [])
            .map((u) => u?.name)
            .filter(Boolean)
            .map(String),
        );

        if (!matchingLineItem) {
          newIndices = currentUploads.reduce((acc, file, index) => {
            if (isBrowserFile(file)) {
              acc.push(index);
            }
            return acc;
          }, []);
        } else {
          newIndices = currentUploads.reduce((acc, file, index) => {
            if (isBrowserFile(file) && !uploadedNames.has(String(file.name))) {
              acc.push(index);
            }
            return acc;
          }, []);
        }

        if (newIndices.length > 0) {
          setUploading(true);
          setUploadProgress(0);

          const filesToUpload = newIndices.map((i) => currentUploads[i]);

          const compressedFiles = await Promise.all(
            filesToUpload.map((f) => compressFile(f)),
          );

          const signedUploads = compressedFiles.map((file, index) => ({
            file,
            requestName: `${index}_${sanitizeUploadFilename(file.name)}`,
          }));

          const filesMapping = buildReturnMediaPresignFiles(
            signedUploads,
            selectedProduct.line_item_id,
          );

          const posts = await handleMediaUpload(filesMapping);

          // Use a reducer instead of Object.fromEntries for older mobile browsers.
          const postMap = posts.data.posts.reduce((acc, post) => {
            acc[post.name] = post.post_data;
            return acc;
          }, {});

          try {
            let completed = 0;
            const total = compressedFiles.length;

            const uploadPromises = signedUploads.map(
              ({ file, requestName }) => {
                const postData = postMap[requestName];
                if (!postData) {
                  throw new Error(`No presigned URL for ${file.name}`);
                }
                return uploadToS3(file, postData, () => {
                  completed += 1;
                  setUploadProgress(Math.round((completed / total) * 100));
                });
              },
            );
            await Promise.all(uploadPromises);
            setUploading(false);
            setUploadProgress(100);

            const currentUploadIds = posts.data.posts.map((post, index) => ({
              media: post.upload_media_id,
              name: signedUploads[index]?.file?.name || post.name,
              type: post.content_type,
              url: getSignedMediaUrl(post),
              media_config_id: signedUploads[index]?.file?.media_config_id,
              capture_code: signedUploads[index]?.file?.capture_code,
              capture_label: signedUploads[index]?.file?.capture_label,
            }));

            const nextUploadIds = getUpdatedUploads(
              selectedProduct.line_item_id,
              currentUploadIds,
            );
            uploadMediaIdsRef.current = nextUploadIds;
            setUploadMediaIds(nextUploadIds);
            const selectedLineUploads =
              nextUploadIds.find(
                (item) =>
                  String(item.line_item_id) ===
                  String(selectedProduct.line_item_id),
              )?.uploads || [];
            const selectedLinePreviews = selectedLineUploads
              .map((mediaItem) => mediaItem.url)
              .filter((url) => typeof url === "string" && url.length > 0);
            updateReasonDraft(draftLineItemId, {
              uploads: selectedLineUploads,
              preview: selectedLinePreviews,
              uploadMediaIds: nextUploadIds,
            });
            setUploads(selectedLineUploads);
            setPreview(selectedLinePreviews);
            return onSuccess();
          } catch (e) {
            setUploading(false);
            throw e;
          }
        } else {
          return onSuccess();
        }
      } else {
        dispatch(setImageUploads([]));
        return onSuccess();
      }
    } catch (err) {
      console.log(err);
      const raw =
        err?.response?.data?.error ??
        err?.response?.data ??
        err?.response ??
        err ??
        "Please try re-uploading the image";

      setUploadError(
        typeof raw === "string" ? raw : JSON.stringify(raw, null, 2),
      );

      setUploadSuccessMessage(null);
      toast.error(err.message || "Error uploading media files");
      logUploadError(err, {}, type);
    } finally {
      setVerificationLoader(false);
      setUploading(false);
    }
  };

  const handleProceed = async (return_type) => {
    if (isOtherReason(selectedReason)) {
      if (!otherReason) {
        toast.error("Please provide additional details", {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
        });
        setVerificationLoader(false);
        return false;
      }
    }
    if (!areRequiredQuestionsAnswered()) {
      toast.error("Please answer the required questions", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      setVerificationLoader(false);
      return false;
    }

    dispatch(addCustomerComment(otherReason || ""));
    let proceedResult = true;
    const onSuccess = () => {
      dispatch(addUploadMediaIds(uploadMediaIdsRef.current || []));
      if (return_type === "exchange") {
        dispatch(addSelectedReason({
          ...selectedReason,
          questionnaire_answers: buildQuestionnaireAnswerPayload(),
        }));
        setUploadSuccessMessage(null);
        dispatch(addProceedBool(true));
        proceedResult = true;
      } else {
        setUploadSuccessMessage(null);
        proceedResult = dispatchReturnedProducts();
      }
    };

    try {
      const result = await processProceed(
        onSuccess,
        `${return_type} proceed button`,
      );
      return result || proceedResult;
    } finally {
      setVerificationLoader(false);
    }
  };

  useEffect(() => {
    handleProceedRef.current = handleProceed;
  });

  useEffect(() => {
    const currentUploads = Array.isArray(uploads) ? uploads : [];
    setImageUploadCount(
      currentUploads.filter((file) => file?.type?.startsWith("image/")).length,
    );
    setVideoUploadCount(
      currentUploads.filter((file) => file?.type?.startsWith("video/")).length,
    );
  }, [uploads]);

  const configuredMedia = Array.isArray(selectedReason?.media_configs)
    ? selectedReason.media_configs
    : [];
  const requiresMedia = reasonRequiresMedia(selectedReason);
  const minPhotosRequired = getReasonMinimumMediaUploads(
    selectedReason,
    "image",
  );
  const minVideosRequired = getReasonMinimumMediaUploads(
    selectedReason,
    "video",
  );
  const uploadedImageCount = Array.isArray(uploads)
    ? uploads.filter((file) => file?.type?.startsWith("image/")).length
    : 0;
  const uploadedVideoCount = Array.isArray(uploads)
    ? uploads.filter((file) => file?.type?.startsWith("video/")).length
    : 0;
  const configuredRequirementsMet = areReturnMediaRequirementsMet(
    configuredMedia,
    uploads,
  );

  const selectedReasonNeedsSubReason =
    selectedReason?.sub_reasons && selectedReason.sub_reasons.length > 0;
  const effectiveAllowedResolutions = effectiveReasonRule.allowed_resolutions || [];
  const isReasonMissing = !selectedReason;
  const isSubReasonMissing = Boolean(
    selectedReasonNeedsSubReason && !selectedSubReason,
  );
  const canShowDetailFields =
    selectedReason &&
    (!selectedReasonNeedsSubReason || selectedSubReason) &&
    (!reasonFirstFlow || selectedReason.selected_resolution);
  const selectedResolution = selectedReason?.selected_resolution;
  const selectedResolutionSettings =
    effectiveResolutionConfig?.[selectedResolution] || {};
  const isCommentRequired = Boolean(
    selectedReason &&
    (selectedReason.reason_code === "other" ||
      (selectedReason.reason_code || "").toLowerCase().includes("other") ||
      ((selectedReason.collect_customer_comment &&
        selectedReason.mandate_customer_comment) ||
        (effectiveResolutionConfig?.[selectedResolution]?.collect_customer_comment &&
          effectiveResolutionConfig?.[selectedResolution]?.mandate_customer_comment))),
  );
  const isCommentMissing = isCommentRequired && !(otherReason || "").trim();
  const isPhotoUploadMissing =
    canShowDetailFields &&
    requiresMedia &&
    uploadedImageCount < minPhotosRequired;
  const isVideoUploadMissing =
    canShowDetailFields &&
    requiresMedia &&
    uploadedVideoCount < minVideosRequired;
  const showPhotoUploadError = validationAttempted && isPhotoUploadMissing;
  const showVideoUploadError = validationAttempted && isVideoUploadMissing;
  const showReasonError = validationAttempted && isReasonMissing;
  const showSubReasonError = validationAttempted && isSubReasonMissing;
  const showRefundMethodError = validationAttempted && reasonFirstFlow &&
    selectedResolution === "refund" && !selectedRefundMethod;
  const showCommentError =
    validationAttempted && isCommentMissing && canShowDetailFields;
  const canProceed =
    canShowDetailFields &&
    !verificationLoader &&
    !uploading &&
    !isCommentMissing &&
    areRequiredQuestionsAnswered() &&
    (!reasonFirstFlow || selectedResolution) &&
    (!reasonFirstFlow || selectedResolution !== "refund" ||
      (refundMethodOptions.length > 0 && Boolean(selectedRefundMethod))) &&
    configuredRequirementsMet &&
    uploads.length >=
      getReasonMinimumConfiguredMediaUploads(selectedReason) &&
    ((uploadedImageCount >= minPhotosRequired &&
      uploadedVideoCount >= minVideosRequired) ||
      !requiresMedia);
  const hasMissingRequiredQuestion =
    validationAttempted && canShowDetailFields && !areRequiredQuestionsAnswered();

  useEffect(() => {
    canProceedRef.current = canProceed;
  }, [canProceed]);

  const ReasonDropdown = ({
    label,
    placeholder,
    value,
    options,
    onSelect,
    id,
    error,
    errorMessage,
  }) => (
    <div
      className={`reason-field reason-select ${
        openDropdown === id ? "open" : ""
      } ${dropdownDirections[id] === "up" ? "drop-up" : ""} ${
        error ? "has-error" : ""
      }`}
    >
      <span>{label}</span>
      <button
        type="button"
        className="reason-select-trigger"
        aria-expanded={openDropdown === id}
        aria-haspopup="listbox"
        onClick={(event) => {
          if (openDropdown === id) {
            setOpenDropdown(null);
            return;
          }

          const triggerRect = event.currentTarget.getBoundingClientRect();
          const spaceBelow = window.innerHeight - triggerRect.bottom;
          const spaceAbove = triggerRect.top;
          const visibleOptionCount = Math.min(options?.length || 0, 5);
          const estimatedMenuHeight = Math.max(
            150,
            visibleOptionCount * 44 + 14,
          );
          const stickyFooterReserve = 120;
          const effectiveSpaceBelow = Math.max(
            0,
            spaceBelow - stickyFooterReserve,
          );
          const openDirection =
            id === "subreason" ||
            effectiveSpaceBelow < estimatedMenuHeight ||
            spaceAbove > effectiveSpaceBelow
              ? "up"
              : "down";

          setDropdownDirections((prev) => ({ ...prev, [id]: openDirection }));
          setOpenDropdown(id);
        }}
      >
        <span>{value?.reason_label || placeholder}</span>
        <KeyboardArrowDownIcon />
      </button>
      {openDropdown === id && (
        <div className="reason-select-menu" role="listbox">
          {options.map((option) => (
            <button
              type="button"
              role="option"
              aria-selected={
                String(value?.reason_code || value?.id || "") ===
                String(option.reason_code || option.id || "")
              }
              key={`${option.id || option.reason_code}-${option.reason_code}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onSelect(option);
                setOpenDropdown(null);
              }}
            >
              {option.reason_label}
            </button>
          ))}
        </div>
      )}
      {error && <p className="reason-field-error">{errorMessage}</p>}
    </div>
  );

  useEffect(() => {
    const nextCanProceed = Boolean(canProceed);
    if (lastValidityRef.current !== nextCanProceed) {
      lastValidityRef.current = nextCanProceed;
      onValidityChangeRef.current?.(nextCanProceed);
    }
  }, [canProceed]);

  useEffect(() => {
    const nextBusy = Boolean(verificationLoader || uploading);
    if (lastBusyRef.current !== nextBusy) {
      lastBusyRef.current = nextBusy;
      onBusyChangeRef.current?.(nextBusy);
    }
  }, [uploading, verificationLoader]);

  useEffect(() => {
    const nextSelectedCount = canShowDetailFields ? quantityInc : 0;
    if (lastSelectedCountRef.current !== nextSelectedCount) {
      lastSelectedCountRef.current = nextSelectedCount;
      onSelectedCountChangeRef.current?.(nextSelectedCount);
    }
  }, [canShowDetailFields, quantityInc]);

  useEffect(() => {
    if (registerSubmit) {
      registerSubmit(async () => {
        if (
          !canProceedRef.current ||
          verificationLoaderRef.current ||
          uploadingRef.current
        ) {
          return null;
        }
        setVerificationLoader(true);
        return handleProceedRef.current?.(
          isExchangeFlowRef.current ? "exchange" : "refund",
        );
      });

      return () => registerSubmit(null);
    }
  }, [registerSubmit]);

  const handleUploadsChange = (nextUploadsValue) => {
    const nextUploads =
      typeof nextUploadsValue === "function"
        ? nextUploadsValue(uploads || [])
        : nextUploadsValue;
    updateReasonDraft(draftLineItemId, { uploads: nextUploads || [] });
    setUploads(nextUploads || []);
  };

  const handlePreviewChange = (nextPreviewValue) => {
    const nextPreview =
      typeof nextPreviewValue === "function"
        ? nextPreviewValue(preview || [])
        : nextPreviewValue;
    updateReasonDraft(draftLineItemId, { preview: nextPreview || [] });
    setPreview(nextPreview || []);
  };

  const handleUploadMediaIdsChange = (nextUploadMediaIdsValue) => {
    const nextUploadMediaIds =
      typeof nextUploadMediaIdsValue === "function"
        ? nextUploadMediaIdsValue(uploadMediaIds || [])
        : nextUploadMediaIdsValue;
    uploadMediaIdsRef.current = nextUploadMediaIds || [];
    updateReasonDraft(draftLineItemId, {
      uploadMediaIds: nextUploadMediaIds || [],
    });
    setUploadMediaIds(nextUploadMediaIds || []);
  };

  const renderReasonFields = () => {
    if (reasonApiError) {
      return (
        <LoadingError
          title="Something went wrong"
          message="We couldn't load the request reasons."
          marginTop="24px"
        />
      );
    }

    return (
      <div className="reason-form">
        <ReasonDropdown
          id="reason"
          label="Reason"
          placeholder={
            reasonLoading ? "Loading reasons..." : "Select reason..."
          }
          value={selectedReason}
          options={reasonLoading ? [] : reasons}
          error={showReasonError}
          errorMessage="Please select a reason."
          onSelect={(reason) => {
            preservePageScroll(() => {
              if (onValidationReset) onValidationReset();
              const selected = reason
                ? { ...reason, ...(reasonFirstFlow ? { reason_mode: "reason_first" } : {}) }
                : null;
              setSelectedReason(selected);
              setSelectedSubReason(null);
              setOtherReason(null);
              const sameReason = selectedReason && reason &&
                String(selectedReason.reason_code || selectedReason.id) ===
                String(reason.reason_code || reason.id);
              const relevantQuestions = new Map((reason?.reason_questions || [])
                .map((mapping) => [String(mapping.question?.id), mapping.question]));
              setQuestionAnswers((answers) => Object.fromEntries(
                Object.entries(answers).filter(([id]) => relevantQuestions.has(id))
                  .map(([id, answer]) => {
                    const primaryAnswer = { ...answer };
                    if (!sameReason || !additionalDetailsActive(relevantQuestions.get(id), answer)) {
                      delete primaryAnswer.additional_comment;
                    }
                    return [id, primaryAnswer];
                  }),
              ));
              dispatch(addCustomerComment(null));
              dispatch(addSelectedReason(selected));
              dispatch(addSelectedSubReason(null));
            });
          }}
        />

        {selectedReason?.comment && (
          <p className="reason-comment-note">{selectedReason.comment}</p>
        )}

        {reasonFirstFlow && selectedReason &&
          (!selectedReasonNeedsSubReason || selectedSubReason) && (
          <div className="reason-resolution-field">
            <span>Available options</span>
            <div className="reason-resolution-options">
              {effectiveAllowedResolutions
                .filter((resolution) => availableResolutions.includes(resolution))
                .map((resolution) => (
                <button
                  type="button"
                  key={resolution}
                  className={selectedResolution === resolution ? "active" : ""}
                  aria-pressed={selectedResolution === resolution}
                  onClick={() => {
                    const nextReason = {
                      ...selectedReason,
                      ...(reasonFirstFlow ? { reason_mode: "reason_first" } : {}),
                      selected_resolution: resolution,
                    };
                    setSelectedReason(nextReason);
                    dispatch(addSelectedReason(nextReason));
                    onResolutionSelect?.(resolution);
                  }}
                >
                  {resolution === "connect_to_agent"
                    ? "Connect to Agent"
                    : resolution === "refund"
                    ? "Refund"
                    : resolution === "replacement"
                      ? "Replacement"
                      : "Exchange"}
                </button>
              ))}
            </div>
            {selectedResolution === "refund" && (
              <div className="reason-refund-method-field">
                <span className="reason-refund-method-label">Refund method</span>
                {refundMethodOptions.length === 1 ? (
                  <div className="reason-refund-method-single" aria-label="Refund method">
                    {refundMethodOptions[0].label}
                  </div>
                ) : refundMethodOptions.length > 1 ? (
                  <select
                    id="reason-first-refund-method"
                    aria-label="Refund method"
                    value={selectedRefundMethod}
                    onChange={(event) => {
                      const method = event.target.value;
                      setSelectedRefundMethod(method);
                      updateReasonDraft(draftLineItemId, { customerPreferredRefundMethod: method });
                    }}
                  >
                    <option value="" disabled>Select a refund method</option>
                    {refundMethodOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                ) : (
                  <p className="reason-field-error" role="alert">
                    No refund method is available for this item. Please contact the store.
                  </p>
                )}
                {showRefundMethodError && refundMethodOptions.length > 1 && (
                  <p className="reason-field-error" role="alert">Please select a refund method.</p>
                )}
              </div>
            )}
            {!effectiveAllowedResolutions.some((resolution) =>
              availableResolutions.includes(resolution),
            ) && <p className="reason-field-error">No configured option is currently available for this item.</p>}
          </div>
        )}

        {selectedReasonNeedsSubReason && (
          <ReasonDropdown
            id="subreason"
            label="Sub-Reason"
            placeholder="Select detail..."
            value={selectedSubReason}
            options={selectedReason.sub_reasons}
            error={showSubReasonError}
            errorMessage="Please select a sub-reason."
            onSelect={(subreason) => {
              preservePageScroll(() => {
                if (onValidationReset) onValidationReset();
                setSelectedSubReason(subreason || null);
                dispatch(addSelectedSubReason(subreason || null));
                const nextRule = getEffectiveReasonRule(selectedReason, subreason);
                if (
                  selectedReason?.selected_resolution &&
                  !(nextRule.allowed_resolutions || []).includes(selectedReason.selected_resolution)
                ) {
                  const nextReason = { ...selectedReason, selected_resolution: null };
                  setSelectedReason(nextReason);
                  dispatch(addSelectedReason(nextReason));
                  onResolutionSelect?.(null);
                }
              });
            }}
          />
        )}

        {canShowDetailFields && requiresMedia && (
          <div className="reason-upload">
            <ImageUpload
              selectedReason={selectedReason}
              uploads={uploads}
              setUploads={handleUploadsChange}
              preview={preview}
              setPreview={handlePreviewChange}
              uploadMediaIds={uploadMediaIds}
              setUploadMediaIds={handleUploadMediaIdsChange}
              setImageUploadCount={setImageUploadCount}
              setVideoUploadCount={setVideoUploadCount}
              imageUploadCount={imageUploadCount}
              videoUploadCount={videoUploadCount}
              lineItemId={selectedProduct.line_item_id}
              photoUploadError={showPhotoUploadError}
              videoUploadError={showVideoUploadError}
              validationAttempted={validationAttempted}
            />
          </div>
        )}

        {canShowDetailFields && getSelectedReasonQuestions().length > 0 && (
          <div className="reason-questionnaire-card">
            <div className="reason-questionnaire-heading">
              <h3 className="reason-label">QUICK QUESTIONS</h3>
            </div>
            <div className="reason-questionnaire">
              {getSelectedReasonQuestions().map((mapping) => {
                const question = mapping.question || {};
                const answer = getQuestionAnswer(question.id);
                const detailsActive = additionalDetailsActive(question, answer);
                const detailsInvalid = validationAttempted && !additionalCommentValid(question, answer);
                const detailsId = `question-details-${selectedProduct.line_item_id}-${question.id}`;
                const missing =
                  validationAttempted &&
                  mapping.required &&
                  !(
                    question.answer_type === "text"
                      ? (answer.answer_text || "").trim()
                      : answer.answer_value !== undefined &&
                        answer.answer_value !== null
                  );

                return (
                  <div
                    className={`reason-question ${missing ? "has-error" : ""}`}
                    key={mapping.id || question.id}
                  >
                    <span>
                      {question.title}
                      {mapping.required ? " *" : ""}
                    </span>
                    {question.help_text && <p>{question.help_text}</p>}
                    {question.answer_type === "text" ? (
                      <textarea
                        aria-label={question.title}
                        placeholder="Add details..."
                        value={answer.answer_text || ""}
                        onChange={(event) =>
                          setQuestionAnswer(question, {
                            answer_text: event.target.value,
                            answer_value: event.target.value,
                            answer_label: event.target.value,
                          })
                        }
                      />
                    ) : (
                      <div className="reason-question-options">
                        {(question.answer_type === "yes_no"
                          ? [
                              { value: "yes", label: "Yes" },
                              { value: "no", label: "No" },
                            ]
                          : question.options || []
                        ).map((option) => {
                          const isActive =
                            String(answer.answer_value ?? "") ===
                            String(option.value ?? "");
                          return (
                            <button
                              type="button"
                              key={option.id || option.value}
                              aria-pressed={isActive}
                              className={isActive ? "active" : ""}
                              onClick={() =>
                                setQuestionAnswer(question, {
                                  option_id: option.id || null,
                                  answer_value: option.value,
                                  answer_label: option.label,
                                })
                              }
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {detailsActive && (
                      <label className="reason-question-details" htmlFor={detailsId}>
                        Additional details{question.additional_details.required ? " *" : " (optional)"}
                        {question.additional_details.help_text && (
                          <span id={`${detailsId}-help`} className="reason-question-details-help">
                            {question.additional_details.help_text}
                          </span>
                        )}
                        <textarea
                          id={detailsId}
                          aria-label={`Additional details for ${question.title}`}
                          aria-describedby={question.additional_details.help_text ? `${detailsId}-help` : undefined}
                          aria-required={Boolean(question.additional_details.required)}
                          aria-invalid={detailsInvalid}
                          maxLength={MAX_ADDITIONAL_COMMENT_LENGTH}
                          value={answer.additional_comment || ""}
                          onChange={(event) => setQuestionAnswer(question, { additional_comment: event.target.value })}
                        />
                        {detailsInvalid && (
                          <span className="reason-field-error" role="alert">
                            {String(answer.additional_comment || "").trim().length > MAX_ADDITIONAL_COMMENT_LENGTH
                              ? "Use 2,000 characters or fewer."
                              : "Please provide additional details."}
                          </span>
                        )}
                      </label>
                    )}
                    {missing && (
                      <p className="reason-field-error">
                        Please answer this question.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            {hasMissingRequiredQuestion && (
              <div className="reason-required-summary" role="alert">
                Please complete all required fields.
              </div>
            )}
          </div>
        )}

        {canShowDetailFields &&
          (selectedReason.reason_code === "other" ||
            (selectedReason.reason_code || "")
              .toLowerCase()
              .includes("other") ||
            selectedReason.collect_customer_comment ||
            selectedResolutionSettings.collect_customer_comment) && (
            <label
              className={`reason-field reason-comments ${
                showCommentError ? "has-error" : ""
              }`}
            >
              <span>Comments</span>
              <textarea
                placeholder="Tell us more about the issue"
                value={otherReason || ""}
                onChange={(e) => {
                  const nextComment = e.target.value;
                  updateReasonDraft(draftLineItemId, {
                    comment: nextComment || "",
                  });
                  setOtherReason(nextComment);
                }}
              />
              {showCommentError && (
                <p className="reason-field-error">
                  Please provide additional details
                </p>
              )}
            </label>
          )}

        {uploading && (
          <div className="reason-upload-progress">
            <progress max="100" value={uploadProgress} />
            <p>{uploadProgress}% uploaded</p>
          </div>
        )}

        {uploadSuccessMessage !== null && (
          <p className="reason-success">{uploadSuccessMessage}</p>
        )}

        {uploadError !== null && <p className="reason-error">{uploadError}</p>}
      </div>
    );
  };

  return (
    <StyledReasonWrapper
      primary_color={merchantConfig.primary_theme_color}
      rounded={merchantConfig.use_rounded_edge_buttons}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      {renderReasonFields()}
    </StyledReasonWrapper>
  );
}

export default Reason;
